// src/components/test/EdgeFunctionTester.tsx
import React, { useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { v4 as uuidv4 } from "uuid";

export const EdgeFunctionTester = () => {
  // State variables
  const [activeTab, setActiveTab] = useState("direct");
  const [file, setFile] = useState<File | null>(null);
  const [photographerName, setPhotographerName] = useState("Test Photographer");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [result, setResult] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [watermarkedPreview, setWatermarkedPreview] = useState<string | null>(
    null
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);

      // Create preview of original
      const reader = new FileReader();
      reader.onload = () => {
        setOriginalPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);

      // Reset other states
      setWatermarkedPreview(null);
      setResult(null);
      setErrorMessage(null);
      setStatus("idle");
    }
  };

  // Test direct fetch to edge function
  const testDirectFetch = async () => {
    if (!file) {
      setErrorMessage("Please select a file first");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);
    setResult(null);

    try {
      // Get the auth session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("You must be logged in to test the edge function");
      }

      // Generate a unique ID for the file
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileId = `${uuidv4()}.${fileExt}`;

      // First upload the file to temporary storage
      const { error: uploadError } = await supabase.storage
        .from("temp-originals")
        .upload(fileId, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Log successful upload
      console.log("File uploaded successfully with ID:", fileId);

      // Call the edge function directly
      const response = await fetch(`${supabase.functions.url}/watermark`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          fileId,
          photographerName,
          maxWidth: 800,
          maxHeight: 600,
          opacity: 0.5,
        }),
      });
      console.log("Edge function response:", response);
      const responseData = await response.json();
      console.log("Edge function response:", responseData);

      if (!response.ok) {
        throw new Error(`Edge function error: ${JSON.stringify(responseData)}`);
      }

      setResult(responseData);

      // Load the watermarked image if available
      if (responseData.watermarkUrl) {
        console.log("Watermarked image URL:", responseData.watermarkUrl);
        setWatermarkedPreview(responseData.watermarkUrl);
      }

      setStatus("success");
    } catch (error) {
      console.error("Error testing edge function:", error);
      setErrorMessage(error.message);
      setStatus("error");
    }
  };

  // Test using Supabase client functions.invoke
  const testSupabaseInvoke = async () => {
    if (!file) {
      setErrorMessage("Please select a file first");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);
    setResult(null);

    try {
      // Generate a unique ID for the file
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileId = `${uuidv4()}.${fileExt}`;

      // First upload the file to temporary storage
      const { error: uploadError } = await supabase.storage
        .from("temp-originals")
        .upload(fileId, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();
      console.log("Session:", session);
      // Call the edge function using Supabase client
      const { data, error } = await supabase.functions.invoke("watermark", {
        body: {
          fileId,
          photographerName,
          maxWidth: 800,
          maxHeight: 600,
          opacity: 0.5,
        },
      });

      if (error) {
        throw error;
      }

      setResult(data);

      // Load the watermarked image if available
      if (data?.watermarkUrl) {
        setWatermarkedPreview(data.watermarkUrl);
      }

      setStatus("success");
    } catch (error) {
      console.error("Error testing edge function:", error);
      setErrorMessage(error.message);
      setStatus("error");
    }
  };

  // Test the WatermarkService
  const testWatermarkService = async () => {
    if (!file) {
      setErrorMessage("Please select a file first");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);
    setResult(null);

    try {
      // Import watermark service dynamically
      const { WatermarkService } = await import("@/services/watermarkService");

      // Generate watermark using the service
      const watermarkedBlob = await WatermarkService.generateWatermark(file, {
        photographerName,
        maxWidth: 800,
        maxHeight: 600,
        opacity: 0.5,
      });

      // Create an object URL for preview
      const watermarkedUrl = URL.createObjectURL(watermarkedBlob);
      setWatermarkedPreview(watermarkedUrl);

      setResult({
        success: true,
        method: "WatermarkService",
        blobSize: watermarkedBlob.size,
        blobType: watermarkedBlob.type,
      });

      setStatus("success");
    } catch (error) {
      console.error("Error testing WatermarkService:", error);
      setErrorMessage(error.message);
      setStatus("error");
    }
  };

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Edge Function Tester</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* File Selection */}
          <div className="space-y-2">
            <div className="font-medium">1. Select an image file</div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={status === "loading"}
              >
                Select Image
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              {file && (
                <div className="text-sm">
                  Selected: {file.name} ({Math.round(file.size / 1024)} KB)
                </div>
              )}
            </div>
          </div>

          {/* Photographer Name */}
          <div className="space-y-2">
            <div className="font-medium">
              2. Enter photographer name for watermark
            </div>
            <Input
              value={photographerName}
              onChange={(e) => setPhotographerName(e.target.value)}
              placeholder="Photographer Name"
              disabled={status === "loading"}
            />
          </div>

          {/* Test Options */}
          <div className="space-y-2">
            <div className="font-medium">3. Choose test method</div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="direct">Direct Fetch</TabsTrigger>
                <TabsTrigger value="invoke">Supabase Invoke</TabsTrigger>
                <TabsTrigger value="service">WatermarkService</TabsTrigger>
              </TabsList>

              <TabsContent value="direct" className="pt-4">
                <div className="text-sm text-gray-500 mb-4">
                  Tests the edge function by making a direct fetch call.
                </div>
                <Button
                  onClick={testDirectFetch}
                  disabled={!file || status === "loading"}
                >
                  {status === "loading" ? "Testing..." : "Test Direct Fetch"}
                </Button>
              </TabsContent>

              <TabsContent value="invoke" className="pt-4">
                <div className="text-sm text-gray-500 mb-4">
                  Tests the edge function using supabase.functions.invoke().
                </div>
                <Button
                  onClick={testSupabaseInvoke}
                  disabled={!file || status === "loading"}
                >
                  {status === "loading" ? "Testing..." : "Test Supabase Invoke"}
                </Button>
              </TabsContent>

              <TabsContent value="service" className="pt-4">
                <div className="text-sm text-gray-500 mb-4">
                  Tests the WatermarkService which has fallback capabilities.
                </div>
                <Button
                  onClick={testWatermarkService}
                  disabled={!file || status === "loading"}
                >
                  {status === "loading"
                    ? "Testing..."
                    : "Test WatermarkService"}
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          {/* Result */}
          {status !== "idle" && (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="font-medium">
                Test Results:
                {status === "loading" && "Running test..."}
                {status === "success" && "Success!"}
                {status === "error" && "Error!"}
              </div>

              {errorMessage && (
                <div className="text-red-500 text-sm">{errorMessage}</div>
              )}

              {result && (
                <div className="bg-gray-100 p-3 rounded-md text-sm max-h-40 overflow-auto">
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}

              {/* Image Preview */}
              {(originalPreview || watermarkedPreview) && (
                <div className="grid grid-cols-2 gap-4">
                  {originalPreview && (
                    <div>
                      <div className="text-sm font-medium mb-2">Original</div>
                      <img
                        src={originalPreview}
                        alt="Original"
                        className="w-full h-auto rounded-md border"
                      />
                    </div>
                  )}
                  {watermarkedPreview && (
                    <div>
                      <div className="text-sm font-medium mb-2">
                        Watermarked
                      </div>
                      <img
                        src={watermarkedPreview}
                        alt="Watermarked"
                        className="w-full h-auto rounded-md border"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
